import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import { blogPosts, blogCategories } from '@/mocks/blogPosts';

const generateBlogSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Thoughtic Tech Blog",
    "description": "Deep dives into system architecture, performance engineering, hiring strategy, and the engineering culture that ships great software.",
    "url": "https://thoughtic.com/blog",
    "blogPost": blogPosts.map((post) => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "author": {
        "@type": "Person",
        "name": post.author
      },
      "datePublished": post.date,
      "url": `https://thoughtic.com/blog/${post.slug}`,
      "keywords": post.tags.join(', ')
    }))
  };
  return JSON.stringify(schema);
};

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

/* ── Blog Hero ── */
function BlogHero() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-28 pb-16 md:pt-40 md:pb-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0">
        <img
          src="src/images/blog/scaling-Node-js.jpg"
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/60 via-navy-900/70 to-navy-900/90"></div>
      </div>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6"
          >
            <i className="ri-article-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Engineering Insights</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4 md:mb-6"
          >
            Thoughtic
            <span className="text-primary-400"> Tech Blog</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-white/70 max-w-2xl mx-auto"
          >
            Deep dives into system architecture, performance engineering, hiring strategy, and the engineering culture that ships great software.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-8 flex flex-wrap justify-center gap-3"
          >
            <span className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-xs font-medium text-white/70">
              <i className="ri-code-box-line mr-1.5"></i>
              10+ Articles
            </span>
            <span className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-xs font-medium text-white/70">
              <i className="ri-user-star-line mr-1.5"></i>
              Written by Staff Engineers
            </span>
            <span className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-xs font-medium text-white/70">
              <i className="ri-time-line mr-1.5"></i>
              Updated Weekly
            </span>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/* ── Featured Article Card (large) ── */
function FeaturedArticle({ post, index }: { post: (typeof blogPosts)[0]; index: number }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.08, ...springTransition }}
      className="group bg-gray rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-xl hover:shadow-navy-900/5 transition-all duration-500 overflow-hidden"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="relative h-64 lg:h-auto overflow-hidden">
          <img
          src="http://localhost:3000/thoughtic-solutions.png"
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900/30 to-transparent"></div>
          <div className="absolute top-4 left-4">
            <span className="px-3 py-1.5 bg-primary-500 text-white text-xs font-semibold rounded-lg">
              Featured
            </span>
          </div>
        </div>
        <div className="p-6 md:p-8 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-3">
            <span className="px-2.5 py-1 bg-primary-50 border border-primary-200 rounded-md text-xs font-semibold text-primary-700">
              {post.category}
            </span>
            <span className="text-xs text-gray-400">{post.date}</span>
            <span className="text-xs text-gray-400">· {post.readTime}</span>
          </div>
          <h3 className="text-xl md:text-2xl font-bold text-navy-900 mb-3 leading-tight group-hover:text-primary-600 transition-colors">
            {post.title}
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed mb-4">
            {post.excerpt}
          </p>
          <div className="flex flex-wrap gap-2 mb-5">
            {post.tags.map((tag) => (
              <span key={tag} className="px-2 py-1 bg-gray-100 rounded-md text-[11px] font-medium text-gray-500">
                #{tag}
              </span>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-navy-900 flex items-center justify-center text-white text-xs font-bold">
                {post.author.split(' ').map((n) => n[0]).join('')}
              </div>
              <div>
                <div className="text-sm font-semibold text-navy-900">{post.author}</div>
                <div className="text-[11px] text-gray-500">{post.authorRole}</div>
              </div>
            </div>
            <button
              onClick={() => setExpanded(!expanded)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              {expanded ? 'Show Less' : 'Read Article'}
              <i className={expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-6 md:px-8 pb-8 pt-2 border-t border-gray-100">
              <div className="max-w-3xl">
                {post.content.map((paragraph, i) => (
                  <p key={i} className="text-sm text-gray-700 leading-relaxed mb-4 last:mb-0">
                    {paragraph}
                  </p>
                ))}
                <div className="mt-6 flex items-center gap-3">
                  <Link
                    to="/case-studies"
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-navy-900 text-white text-sm font-semibold rounded-xl hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
                  >
                    See Real Results
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link
                    to="/talent"
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 text-navy-900 text-sm font-semibold rounded-xl hover:border-gray-300 transition-all cursor-pointer whitespace-nowrap"
                  >
                    Hire Our Engineers
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ── Standard Article Card ── */
function ArticleCard({ post, index }: { post: (typeof blogPosts)[0]; index: number }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 25 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.06, ...springTransition }}
      className="group bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg hover:shadow-navy-900/5 transition-all duration-300 overflow-hidden flex flex-col"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={`http://localhost:3000/thoughtic-solutions.png`}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900/40 to-transparent"></div>
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 bg-white/90 backdrop-blur-sm text-navy-900 text-[11px] font-semibold rounded-md">
            {post.category}
          </span>
        </div>
      </div>
      <div className="p-5 md:p-6 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-2.5">
          <span className="text-xs text-gray-400">{post.date}</span>
          <span className="text-xs text-gray-300">·</span>
          <span className="text-xs text-gray-400">{post.readTime}</span>
        </div>
        <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2 leading-tight group-hover:text-primary-600 transition-colors line-clamp-2">
          {post.title}
        </h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3 flex-1">
          {post.excerpt}
        </p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {post.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-gray-100 rounded text-[10px] font-medium text-gray-500">
              #{tag}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-gray-50">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-navy-900 flex items-center justify-center text-white text-[10px] font-bold">
              {post.author.split(' ').map((n) => n[0]).join('')}
            </div>
            <span className="text-xs font-medium text-gray-600">{post.author}</span>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            {expanded ? 'Less' : 'Read'}
            <i className={expanded ? ' ri-arrow-up-s-line ml-1' : ' ri-arrow-down-s-line ml-1'}></i>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-5 md:px-6 pb-5 pt-2 border-t border-gray-100">
              {post.content.map((paragraph, i) => (
                <p key={i} className="text-sm text-gray-700 leading-relaxed mb-3 last:mb-0">
                  {paragraph}
                </p>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ── Main Blog Section ── */
function BlogSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.02 });
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? blogPosts
    : blogPosts.filter((p) => p.category === activeCategory);

  const featured = filtered.filter((p) => p.featured);
  const standard = filtered.filter((p) => !p.featured);

  return (
    <section className="py-16 md:py-24 bg-gray-50/50 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-primary-100/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-amber-50/30 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.6 }}
          className="text-center mb-10 md:mb-14"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 shadow-sm"
          >
            <i className="ri-book-open-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Latest Articles</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 mb-4"
          >
            Engineering Deep
            <span className="text-primary-600"> Dives</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto"
          >
            Written by the engineers who build the systems. No fluff. Just production-tested insights.
          </motion.p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, ...springTransition }}
          className="flex flex-wrap justify-center gap-2 mb-10 md:mb-14"
        >
          {['All', ...blogCategories].map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-navy-900 text-white shadow-lg'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              {cat}
            </button>
          ))}
        </motion.div>

        {/* Featured Articles */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory + '-featured'}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="space-y-8 md:space-y-10 mb-10 md:mb-14"
          >
            {featured.map((post, i) => (
              <FeaturedArticle key={post.slug} post={post} index={i} />
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Standard Articles Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory + '-grid'}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
          >
            {standard.map((post, i) => (
              <ArticleCard key={post.slug} post={post} index={i} />
            ))}
          </motion.div>
        </AnimatePresence>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <i className="ri-article-line text-4xl text-gray-300 mb-3 block"></i>
            <p className="text-gray-500">No articles found in this category.</p>
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Newsletter CTA ── */
function BlogNewsletter() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      data.append(key, value as string);
    }
    fetch(form.action, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    })
      .then(() => {
        setSubmitted(true);
        setEmail('');
      })
      .catch(() => {
        // Silently handle — user still sees success UI
        setSubmitted(true);
        setEmail('');
      });
  };

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.6 }}
          className="bg-navy-900 rounded-3xl p-8 md:p-12 lg:p-16 relative overflow-hidden"
        >
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-20 -right-20 w-[400px] h-[400px] bg-primary-500/15 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-[300px] h-[300px] bg-amber-500/10 rounded-full blur-3xl"></div>
          </div>

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/20 rounded-full mb-4">
                <i className="ri-mail-send-line text-primary-400 text-sm"></i>
                <span className="text-xs font-medium text-white/80">Weekly Digest</span>
              </div>
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold text-white mb-3">
                Get Engineering Insights<br />
                <span className="text-primary-400">In Your Inbox</span>
              </h3>
              <p className="text-sm md:text-base text-white/70 leading-relaxed">
                Join 4,200+ engineers and engineering leaders who get our weekly deep dives on system design, performance, and hiring strategy.
              </p>
            </div>
            <div>
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 text-center"
                >
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-check-line text-emerald-400 text-2xl"></i>
                  </div>
                  <h4 className="text-lg font-bold text-white mb-1">You are subscribed!</h4>
                  <p className="text-sm text-white/60">Watch your inbox for the next edition.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3" method="POST" action="https://readdy.ai/api/form/d8trd0kuatl81q3tfog0" encType="application/x-www-form-urlencoded" data-readdy-form>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    name="email"
                    placeholder="you@company.com"
                    required
                    className="w-full px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/20 text-sm"
                  />
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" className="website-alt-field" />
                  <button
                    type="submit"
                    className="w-full px-5 py-4 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    Subscribe to Weekly Digest
                    <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                  <p className="text-[11px] text-white/40 text-center">
                    No spam. Unsubscribe anytime. We respect your inbox.
                  </p>
                </form>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ── Page ── */
export default function BlogPage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = generateBlogSchema();
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  return (
    <PageLayout title="Tech Blog" description="Engineering deep dives on system design, hiring strategy, and performance optimization. Written by the engineers who build the systems.">
      <BlogHero />
      <BlogSection />
      <BlogNewsletter />
    </PageLayout>
  );
}